import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, XCircle, Loader2, Clock, CheckCircle, AlertCircle, Star, Video, FileText, Phone, Calendar } from 'lucide-react';
import { Avatar, toast } from '@clareefai/ui';
import {
  useGetSpecificRewardCommitmentDetailQuery,
  useCancelSpecificRewardCommitmentMutation,
} from '../../store/vendorApi';
import CommitmentTaskBadge from '../../components/rewards/specific/CommitmentTaskBadge';
import SendReminderModal from '../../components/rewards/specific/SendReminderModal';
import type { CommitmentStatus, CommitmentTask } from '@clareefai/types';

const STATUS_COLORS: Record<CommitmentStatus, { color: string; icon: React.ComponentType<any> }> = {
  PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  IN_PROGRESS: { color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  COMPLETED: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  EXPIRED: { color: 'bg-red-100 text-red-800', icon: XCircle },
  CANCELLED: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
  REWARD_PAID: { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
};

const TASK_ICONS: Record<string, React.ComponentType<any>> = {
  REVIEW: Star,
  VIDEO: Video,
  USE_CASE: FileText,
  REFERENCE_CALL: Phone,
};

const SpecificRewardDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const STATUS_LABELS: Record<CommitmentStatus, string> = {
    PENDING: t('rewards.specific.detail.status.pending'),
    IN_PROGRESS: t('rewards.specific.detail.status.inProgress'),
    COMPLETED: t('rewards.specific.detail.status.completed'),
    EXPIRED: t('rewards.specific.detail.status.expired'),
    CANCELLED: t('rewards.specific.detail.status.cancelled'),
    REWARD_PAID: t('rewards.specific.detail.status.rewardPaid'),
  };

  const getRewardLabel = (unit: string, amount: number) => {
    switch (unit) {
      case 'percentage': return t('rewards.specific.detail.rewardUnits.percentage', { amount });
      case 'credits': return t('rewards.specific.detail.rewardUnits.credits', { amount });
      case 'free_months': return t('rewards.specific.detail.rewardUnits.freeMonths', { amount });
      default: return `${amount} ${unit}`;
    }
  };

  const { data: commitment, isLoading } = useGetSpecificRewardCommitmentDetailQuery(id!, { skip: !id });
  const [cancelCommitment, { isLoading: isCancelling }] = useCancelSpecificRewardCommitmentMutation();
  const [showReminderModal, setShowReminderModal] = useState(false);

  if (isLoading || !commitment) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#4339A0]" />
      </div>
    );
  }

  const statusCfg = STATUS_COLORS[commitment.status];
  const statusLabel = STATUS_LABELS[commitment.status];
  const StatusIcon = statusCfg.icon;
  const completedTasks = commitment.tasks.filter((t) => t.is_completed).length;
  const totalTasks = commitment.tasks.length;
  const progressPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const daysRemaining = Math.max(0, Math.ceil((new Date(commitment.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  const isOverdue = new Date(commitment.deadline) < new Date() && commitment.status !== 'COMPLETED' && commitment.status !== 'REWARD_PAID';
  const canSendReminder = commitment.status === 'PENDING' || commitment.status === 'IN_PROGRESS';
  const canCancel = commitment.status === 'PENDING' || commitment.status === 'IN_PROGRESS';

  const handleCancel = async () => {
    if (!confirm(t('rewards.specific.detail.confirmCancel'))) return;
    try {
      await cancelCommitment(commitment.id).unwrap();
      toast.success(t('rewards.specific.detail.commitmentCancelled'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('rewards.specific.detail.failedToCancel'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/rewards')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <Avatar src={commitment.promoter_avatar} name={commitment.promoter_name || commitment.promoter_email} size="lg" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{commitment.promoter_name || t('rewards.specific.detail.unknown')}</h1>
              <p className="text-sm text-gray-500">{commitment.promoter_email}</p>
              {commitment.promoter_company && <p className="text-xs text-gray-400">{commitment.promoter_company}</p>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canSendReminder && (
            <button
              onClick={() => setShowReminderModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors text-sm font-medium"
            >
              <Send className="w-4 h-4" /> {t('rewards.specific.detail.sendReminder')}
            </button>
          )}
          {canCancel && (
            <button
              onClick={handleCancel}
              disabled={isCancelling}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
            >
              {isCancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              {t('rewards.specific.detail.cancel')}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Reward Summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">{t('rewards.specific.detail.rewardSummary')}</h2>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                <StatusIcon className="w-3.5 h-3.5" />
                {statusLabel}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">{t('rewards.specific.detail.rewardValue')}</p>
                <p className="font-semibold text-gray-900">{getRewardLabel(commitment.discount_unit, commitment.discount_amount)}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('rewards.specific.detail.startDate')}</p>
                <p className="font-medium text-gray-900">{new Date(commitment.start_date).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('rewards.specific.detail.dueDate')}</p>
                <p className={`font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>{new Date(commitment.deadline).toLocaleDateString()}</p>
              </div>
              {commitment.additional_note && (
                <div>
                  <p className="text-gray-500">{t('rewards.specific.detail.note')}</p>
                  <p className="font-medium text-gray-900 text-xs">{commitment.additional_note}</p>
                </div>
              )}
            </div>
          </div>

          {/* Task Progress */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">{t('rewards.specific.detail.taskProgress')}</h2>
              <span className="text-sm text-gray-500">{t('rewards.specific.detail.tasksCompleted', { completed: completedTasks, total: totalTasks })}</span>
            </div>
            <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#4339A0] to-indigo-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="space-y-3">
              {commitment.tasks.map((task: CommitmentTask, idx: number) => {
                const TaskIcon = TASK_ICONS[task.type] || Star;
                return (
                  <div key={idx} className={`flex items-center justify-between p-4 rounded-lg border ${task.is_completed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${task.is_completed ? 'bg-green-100' : 'bg-gray-200'}`}>
                        {task.is_completed ? <CheckCircle className="w-4 h-4 text-green-600" /> : <TaskIcon className="w-4 h-4 text-gray-400" />}
                      </div>
                      <div>
                        <CommitmentTaskBadge type={task.type} />
                        <p className="text-xs text-gray-500 mt-1">{t('rewards.specific.detail.taskCounts', { completed: task.completed_count, required: task.required_count })}</p>
                      </div>
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${task.is_completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {task.is_completed ? t('rewards.specific.detail.completed') : t('rewards.specific.detail.pending')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Time Remaining */}
          <div className={`rounded-xl border p-5 ${isOverdue ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Clock className={`w-5 h-5 ${isOverdue ? 'text-red-500' : 'text-gray-400'}`} />
              <h3 className="font-semibold text-gray-900">{t('rewards.specific.detail.timeRemaining')}</h3>
            </div>
            <p className={`text-3xl font-bold ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
              {isOverdue ? t('rewards.specific.detail.overdue') : t('rewards.specific.detail.daysRemaining', { days: daysRemaining })}
            </p>
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {t('rewards.specific.detail.dueOn', { date: new Date(commitment.deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) })}
            </p>
          </div>

          {/* Reminder History */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-3">{t('rewards.specific.detail.activity')}</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700">{t('rewards.specific.detail.commitmentCreated')}</p>
                  <p className="text-gray-400">{new Date(commitment.created_at).toLocaleString()}</p>
                </div>
              </div>
              {commitment.invitation_sent_at && (
                <div className="flex items-start gap-2 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-700">{t('rewards.specific.detail.invitationSent')}</p>
                    <p className="text-gray-400">{new Date(commitment.invitation_sent_at).toLocaleString()}</p>
                  </div>
                </div>
              )}
              {commitment.last_reminder_sent_at && (
                <div className="flex items-start gap-2 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-700">{t('rewards.specific.detail.lastReminderSent')}</p>
                    <p className="text-gray-400">{new Date(commitment.last_reminder_sent_at).toLocaleString()}</p>
                  </div>
                </div>
              )}
              {commitment.completed_at && (
                <div className="flex items-start gap-2 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-700">{t('rewards.specific.detail.allTasksCompleted')}</p>
                    <p className="text-gray-400">{new Date(commitment.completed_at).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Send Reminder Modal */}
      {showReminderModal && (
        <SendReminderModal
          commitmentId={commitment.id}
          promoterName={commitment.promoter_name}
          tasks={commitment.tasks}
          deadline={commitment.deadline}
          onClose={() => setShowReminderModal(false)}
        />
      )}
    </div>
  );
};

export default SpecificRewardDetail;
